import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./DocumentVerification.scss";
import Logo from "../../iamges/logo.png";
import {
  login,
  signup,
  verifyEmailCode,
  resendVerificationCode,
  loginWithGoogle,
  loginWithApple,
  completeSocialRedirect,
} from "./auth";

const GoogleIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' aria-hidden='true'>
    <path
      fill='#4285F4'
      d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
    />
    <path
      fill='#34A853'
      d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
    />
    <path
      fill='#FBBC05'
      d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z'
    />
    <path
      fill='#EA4335'
      d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z'
    />
  </svg>
);

const AppleIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='currentColor'
    aria-hidden='true'>
    <path d='M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 1.01-2.87-.96.04-2.12.64-2.8 1.44-.59.69-1.11 1.76-.97 2.82 1.07.08 2.15-.55 2.76-1.39z' />
  </svg>
);

const formatSeconds = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const DocumentVerificationLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode: "login" | "signup" | "otp"
  const [mode, setMode] = useState<"login" | "signup" | "otp">("login");

  // Form fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // OTP State
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const otpInputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const [resendCooldown, setResendCooldown] = useState(60);
  const [isResending, setIsResending] = useState(false);

  // Status & Feedback
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<"google" | "apple" | null>(
    null,
  );
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const from =
    (location.state as any)?.from?.pathname || "/document-verification";

  // Resend Countdown Timer
  useEffect(() => {
    let timerId: any = null;
    if (mode === "otp" && resendCooldown > 0) {
      timerId = setInterval(() => {
        setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [mode, resendCooldown]);

  useEffect(() => {
    let active = true;
    completeSocialRedirect()
      .then((session) => {
        if (active && session) navigate(from, { replace: true });
      })
      .catch((err: any) => {
        if (active) setError(err?.message || "Social sign-in failed. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [from, navigate]);

  // Focus first OTP field when entering OTP mode
  useEffect(() => {
    if (mode === "otp") {
      setTimeout(() => {
        otpInputRefs[0].current?.focus();
      }, 150);
    }
  }, [mode]);

  const handleGoogleSignIn = async () => {
    setError("");
    setSuccessMessage("");
    setSocialLoading("google");
    try {
      await loginWithGoogle();
      navigate(from, { replace: true });
    } catch (err: any) {
      if (
        err?.code === "auth/popup-closed-by-user" ||
        err?.code === "auth/cancelled-popup-request" ||
        err?.message?.includes("closed-by-user")
      ) {
        return;
      }
      setError(err.message || "Google sign-in failed. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  };

  const handleAppleSignIn = async () => {
    setError("");
    setSuccessMessage("");
    setSocialLoading("apple");
    try {
      await loginWithApple();
      navigate(from, { replace: true });
    } catch (err: any) {
      if (
        err?.code === "auth/popup-closed-by-user" ||
        err?.code === "auth/cancelled-popup-request" ||
        err?.message?.includes("closed-by-user")
      ) {
        return;
      }
      setError(err.message || "Apple sign-in failed. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  };

  // Sign In Handler
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    try {
      const result = await login(email.trim(), password);

      if (result.requiresVerification) {
        // Do NOT show error! Switch directly to OTP mode and show helpful message
        setError("");
        setSuccessMessage(
          result.message ||
            "Please verify your email. A 4-digit verification code has been sent to your email.",
        );
        setEmail(result.email || email.trim());
        setMode("otp");
        setOtp(["", "", "", ""]);
        setResendCooldown(60);
        return;
      }

      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || "Could not sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Sign Up Handler
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!fullName.trim()) {
      setError("Please enter your full name.");
      return;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!password) {
      setError("Please enter a password.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await signup(fullName.trim(), email.trim(), password);
      setSuccessMessage(
        res.message || "Account created! A 4-digit code has been sent to your email.",
      );
      setMode("otp");
      setOtp(["", "", "", ""]);
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.message || "Could not create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // OTP Change Handler
  const handleOtpChange = (index: number, value: string) => {
    const cleanVal = value.replace(/\D/g, "");
    if (!cleanVal) {
      const nextOtp = [...otp];
      nextOtp[index] = "";
      setOtp(nextOtp);
      return;
    }

    // Single digit input
    const nextOtp = [...otp];
    nextOtp[index] = cleanVal.slice(-1);
    setOtp(nextOtp);

    // Auto advance to next input
    if (index < 3 && cleanVal.length > 0) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  // OTP Keydown (Backspace navigation)
  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
  };

  // OTP Paste Handler
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const digits = pasted.slice(0, 4).split("");
    const nextOtp = [...otp];
    digits.forEach((digit, i) => {
      if (i < 4) nextOtp[i] = digit;
    });
    setOtp(nextOtp);

    const nextFocusIndex = Math.min(digits.length, 3);
    otpInputRefs[nextFocusIndex].current?.focus();
  };

  // OTP Submit Handler
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    const code = otp.join("");
    if (code.length < 4) {
      setError("Please enter the complete 4-digit verification code.");
      return;
    }

    setLoading(true);
    try {
      await verifyEmailCode(email.trim(), code);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP Code Handler
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isResending) return;
    setError("");
    setSuccessMessage("");
    setIsResending(true);

    try {
      await resendVerificationCode(email.trim());
      setSuccessMessage("A fresh verification code has been sent to your email!");
      setResendCooldown(60);
      setOtp(["", "", "", ""]);
      otpInputRefs[0].current?.focus();
    } catch (err: any) {
      setError(err.message || "Failed to resend verification code.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className='dv-root dv-login-root'>
      <div className='dv-login-card dv-card'>
        <div className='dv-login-brand'>
          <img src={Logo} alt='HomeOwner' />
          <span>HomeOwner</span>
        </div>

        {/* View Header */}
        {mode === "login" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <h1 className='dv-h1' style={{ fontSize: 24 }}>
              Sign in to verify tenant documents
            </h1>
            <p className='dv-field-hint' style={{ fontSize: 13 }}>
              Use the same account as the HomeOwner mobile app — your properties,
              tenants, and history are shared.
            </p>
          </div>
        )}

        {mode === "signup" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <h1 className='dv-h1' style={{ fontSize: 24 }}>
              Create your account
            </h1>
            <p className='dv-field-hint' style={{ fontSize: 13 }}>
              Sign up to verify applicant documents and run identity & tamper cross-checks.
            </p>
          </div>
        )}

        {mode === "otp" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type='button'
                className='dv-back-btn'
                onClick={() => {
                  setMode("signup");
                  setError("");
                  setSuccessMessage("");
                }}>
                <i className='ph ph-arrow-left' />
                <span>Back</span>
              </button>
            </div>
            <h1 className='dv-h1' style={{ fontSize: 22, marginTop: 4 }}>
              Enter Verification Code
            </h1>
            <p className='dv-field-hint' style={{ fontSize: 13 }}>
              We've sent a 4-digit code to <strong>{email}</strong>. Enter the code below to complete registration.
            </p>
          </div>
        )}

        {/* Mode Switcher Tabs (Only when not in OTP mode) */}
        {mode !== "otp" && (
          <div className='dv-auth-toggle'>
            <button
              type='button'
              className={`dv-auth-toggle-btn ${
                mode === "login" ? "dv-auth-toggle-btn--active" : ""
              }`}
              onClick={() => {
                setMode("login");
                setError("");
                setSuccessMessage("");
              }}>
              Sign In
            </button>
            <button
              type='button'
              className={`dv-auth-toggle-btn ${
                mode === "signup" ? "dv-auth-toggle-btn--active" : ""
              }`}
              onClick={() => {
                setMode("signup");
                setError("");
                setSuccessMessage("");
              }}>
              Sign Up
            </button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className='dv-login-error'>
            <i className='ph ph-warning-circle' />
            <span>{error}</span>
          </div>
        )}

        {/* Success Alert */}
        {successMessage && (
          <div className='dv-login-success'>
            <i className='ph ph-check-circle' />
            <span>{successMessage}</span>
          </div>
        )}

        {/* -------------------- 1. LOGIN FORM -------------------- */}
        {mode === "login" && (
          <form className='dv-login-form' onSubmit={handleLoginSubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className='dv-field-label' htmlFor='dv-email'>
                Email
              </label>
              <input
                id='dv-email'
                className='dv-input'
                type='email'
                autoComplete='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder='you@example.com'
                required
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className='dv-field-label' htmlFor='dv-password'>
                Password
              </label>
              <div className='dv-password-field'>
                <input
                  id='dv-password'
                  className='dv-input'
                  type={showPassword ? "text" : "password"}
                  autoComplete='current-password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder='••••••••'
                  required
                />
                <button
                  type='button'
                  className='dv-password-toggle'
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}>
                  <i
                    className={`ph ${showPassword ? "ph-eye-slash" : "ph-eye"}`}
                  />
                </button>
              </div>
            </div>

            <button
              type='submit'
              className='dv-btn dv-btn--lg dv-btn--full dv-btn--glow'
              disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {/* -------------------- 2. SIGNUP FORM -------------------- */}
        {mode === "signup" && (
          <form className='dv-login-form' onSubmit={handleSignupSubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className='dv-field-label' htmlFor='dv-fullname'>
                Full Name
              </label>
              <input
                id='dv-fullname'
                className='dv-input'
                type='text'
                autoComplete='name'
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder='e.g. John Doe'
                required
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className='dv-field-label' htmlFor='dv-signup-email'>
                Email
              </label>
              <input
                id='dv-signup-email'
                className='dv-input'
                type='email'
                autoComplete='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder='you@example.com'
                required
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className='dv-field-label' htmlFor='dv-signup-password'>
                Password
              </label>
              <div className='dv-password-field'>
                <input
                  id='dv-signup-password'
                  className='dv-input'
                  type={showPassword ? "text" : "password"}
                  autoComplete='new-password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder='At least 6 characters'
                  required
                />
                <button
                  type='button'
                  className='dv-password-toggle'
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}>
                  <i
                    className={`ph ${showPassword ? "ph-eye-slash" : "ph-eye"}`}
                  />
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className='dv-field-label' htmlFor='dv-signup-confirm-password'>
                Confirm Password
              </label>
              <div className='dv-password-field'>
                <input
                  id='dv-signup-confirm-password'
                  className='dv-input'
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete='new-password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder='Repeat your password'
                  required
                />
                <button
                  type='button'
                  className='dv-password-toggle'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }>
                  <i
                    className={`ph ${
                      showConfirmPassword ? "ph-eye-slash" : "ph-eye"
                    }`}
                  />
                </button>
              </div>
            </div>

            <button
              type='submit'
              className='dv-btn dv-btn--lg dv-btn--full dv-btn--glow'
              disabled={loading}>
              {loading ? "Creating account…" : "Sign up"}
            </button>
          </form>
        )}

        {/* -------------------- 3. OTP VERIFICATION FORM -------------------- */}
        {mode === "otp" && (
          <form className='dv-login-form' onSubmit={handleOtpSubmit}>
            <div className='dv-otp-inputs'>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={otpInputRefs[i]}
                  className={`dv-otp-digit ${digit ? "dv-otp-digit--filled" : ""}`}
                  type='text'
                  inputMode='numeric'
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={handleOtpPaste}
                  autoComplete='one-time-code'
                />
              ))}
            </div>

            <div className='dv-otp-resend-row'>
              <span>Didn't receive the code?</span>
              {resendCooldown > 0 ? (
                <span style={{ fontWeight: 600, color: "var(--dv-orange-600)" }}>
                  Resend in {formatSeconds(resendCooldown)}
                </span>
              ) : (
                <button
                  type='button'
                  className='dv-resend-btn'
                  onClick={handleResendOtp}
                  disabled={isResending}>
                  {isResending ? "Resending…" : "Resend Code"}
                </button>
              )}
            </div>

            <button
              type='submit'
              className='dv-btn dv-btn--lg dv-btn--full dv-btn--glow'
              disabled={loading || otp.join("").length < 4}>
              {loading ? "Verifying…" : "Verify & Continue"}
            </button>

            <div style={{ textAlign: "center", marginTop: 4 }}>
              <button
                type='button'
                className='dv-back-btn'
                onClick={() => {
                  setMode("signup");
                  setError("");
                  setSuccessMessage("");
                }}>
                <span>Need to change your email? Click here</span>
              </button>
            </div>
          </form>
        )}

        {/* Social Authentication (Only in Login or Signup mode) */}
        {mode !== "otp" && (
          <>
            <div className='dv-login-divider'>
              <span>or continue with</span>
            </div>

            <div className='dv-social-buttons'>
              <button
                type='button'
                className='dv-social-btn dv-social-btn--google'
                onClick={handleGoogleSignIn}
                disabled={loading || socialLoading !== null}>
                <GoogleIcon />
                <span>
                  {socialLoading === "google"
                    ? "Signing in with Google…"
                    : "Continue with Google"}
                </span>
              </button>

              <button
                type='button'
                className='dv-social-btn dv-social-btn--apple'
                onClick={handleAppleSignIn}
                disabled={loading || socialLoading !== null}>
                <AppleIcon />
                <span>
                  {socialLoading === "apple"
                    ? "Signing in with Apple…"
                    : "Continue with Apple"}
                </span>
              </button>
            </div>
          </>
        )}

        {/* Footnote */}
        <p className='dv-login-footnote'>
          {mode === "login" ? (
            <>
              Don't have an account?{" "}
              <button
                type='button'
                onClick={() => {
                  setMode("signup");
                  setError("");
                  setSuccessMessage("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--dv-orange-600)",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: "inherit",
                }}>
                Sign up
              </button>
            </>
          ) : mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                type='button'
                onClick={() => {
                  setMode("login");
                  setError("");
                  setSuccessMessage("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--dv-orange-600)",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: "inherit",
                }}>
                Sign in
              </button>
            </>
          ) : (
            "Enter the 4-digit code to access Document Verification."
          )}
        </p>
      </div>
    </div>
  );
};

export default DocumentVerificationLogin;
